import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "../context/AuthContext";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../state/store";
import { fetchCart } from "../state/slices/cartSlice";
import { fetchUserProfileData } from '../state/slices/userProfileSlice';
import './CheckoutPage.css';
import { Address } from '../interfaces/user';
import { CheckoutProduct } from '../interfaces/adminproduct';
import Loading from './Loading';
import { addAddress, deleteAddress, placeOrder, setDefaultAddress } from '../graphql/api/checkoutApi';
import Footer from './footers';
import Breadcrumbs from './Breadcrumbs';
import MainNavbar from '../pages/MainNavbar';
import NoData from './NoData';
import { Trash2 } from 'lucide-react'; 

const CheckoutPage: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { currentUser, loading } = useAuth();

    // Redux state selectors
    const { cart, status: cartStatus } = useSelector((state: RootState) => state.cart);
    const {
        // user: userProfile,
        addresses: userAddresses,
        loading: profileLoading,
        // error: profileError
    } = useSelector((state: RootState) => state.userProfile);
    const products = useSelector((state: RootState) => state.product.items);

    // Local state
    const [activeTab, setActiveTab] = useState(0);
    const [newAddress, setNewAddress] = useState({
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        country: 'India',
        postalCode: ''
    });
    const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
    const [cartProducts, setCartProducts] = useState<CheckoutProduct[]>([]);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isProcessing, setIsProcessing] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('credit-card');
    const [orderPlaced, setOrderPlaced] = useState(false);
    const [showSubnav, setShowSubnav] = useState(false);

    const userId = currentUser?.id || "";
    const isLoggedIn = !!currentUser;

;

    // Fetch data on component mount
    useEffect(() => {
        if (currentUser) {
            dispatch(fetchCart(currentUser.id));
            dispatch(fetchUserProfileData(currentUser.id));
        }
    }, [currentUser, dispatch]);

    // Set default address when addresses are loaded
    useEffect(() => {
        if (userAddresses.length > 0 && !selectedAddress) {
            const defaultAddress = userAddresses.find(addr => addr.isDefault) || userAddresses[0];
            setSelectedAddress(defaultAddress);
        }
    }, [userAddresses, selectedAddress]);

    // Map cart items to products
    useEffect(() => {
        if (!cart || !cart.items || cart.items.length === 0 || products.length === 0) return;

        const mappedProducts: CheckoutProduct[] = cart.items.map(cartItem => {
            const product = products.find(p => p.id === cartItem.productId);
            return {
                id: cartItem.productId,
                name: product?.name || 'Unknown Product',
                price: cartItem.price,
                images: product?.images || [''],
                quantity: cartItem.quantity
            };
        });

        setCartProducts(mappedProducts);
    }, [cart, products]);

    // Scroll to top on tab change
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [activeTab]);



    const handleAddAddress = async () => {
        await addAddress(
            newAddress,
            currentUser,
            dispatch,
            setErrors,
            setNewAddress,
            setActiveTab,
            setSelectedAddress,
            setIsProcessing
        );
    };

    const handlePlaceOrder = async () => {
        await placeOrder(
            selectedAddress,
            currentUser,
            cart,
            cartProducts,
            dispatch,
            navigate,
            setOrderPlaced,
            setIsProcessing
        );
    };

    const totalAmount = cart ? cart.totalPrice : 0;

    if (loading) {
        return <div>Loading...</div>; // or spinner
    }

    if (!currentUser) {
        return (
            <div className="check_user_container">
                <div className="check_user_auth_required">
                    <h2>Authentication Required</h2>
                    <p>Please log in to proceed with checkout</p>
                    <button onClick={() => navigate('/login')}>Login</button>
                </div>
            </div>
        );
    }

    if (cartStatus === 'loading' || profileLoading) {
        return <Loading message='Loading checkout data...' />;
    }

    // if (orderPlaced) {
    //     return (
    //         <div className="check_user_container">
    //             <div className="check_user_order_success">
    //                 <div className="check_user_success_animation">
    //                     <svg className="check_user_checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
    //                         <circle className="check_user_checkmark_circle" cx="26" cy="26" r="25" fill="none" />
    //                         <path className="check_user_checkmark_check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
    //                     </svg>
    //                 </div>
    //                 <h2>Order Placed Successfully!</h2>
    //                 <p>Your order has been confirmed and will be shipped soon.</p>
    //                 <p>Redirecting to order details...</p>
    //             </div>
    //         </div>
    //     );
    // }

    if (!cart || cart.items.length === 0) {
        return (
            <div className="check_user_container">
                <div className="check_user_empty_cart">
                    <h2>Your cart is empty</h2>
                    <p>There are no items to checkout.</p>
                    <button onClick={() => navigate('/')}>Continue Shopping</button>
                </div>
            </div>
        );
    }

    return (
        <>
            <MainNavbar
                searchQuery=""
                setSearchQuery={() => { }}
                isLoggedIn={isLoggedIn}
                showSubnav={showSubnav}
                setShowSubnav={setShowSubnav}
                userId={userId}
                disableSearch={true} // Disable search in cart page
            />
            <Breadcrumbs />
            <div className="check_user_container">
                <div className="check_user_tabs">
                    <div
                        className={`check_user_step ${activeTab === 0 ? 'active' : ''} ${activeTab > 0 ? 'complete' : ''}`}
                        onClick={() => setActiveTab(0)}
                    >
                        <div className="circle">1</div>
                        <span className="label">Address</span>
                    </div>

                    <div className={`step-line ${activeTab >= 1 ? 'step-line-active' : ''}`} />

                    <div
                        className={`check_user_step ${activeTab === 1 ? 'active' : ''} ${activeTab > 1 ? 'complete' : ''} ${activeTab >= 1 ? '' : 'disabled'}`}
                        onClick={() => activeTab >= 1 && setActiveTab(1)}
                    >
                        <div className="circle">2</div>
                        <span className="label">Summary</span>
                    </div>

                    <div className={`step-line ${activeTab >= 2 ? 'step-line-active' : ''}`} />

                    <div
                        className={`check_user_step ${activeTab === 2 ? 'active' : ''} ${activeTab >= 2 ? '' : 'disabled'}`}
                        onClick={() => activeTab >= 2 && setActiveTab(2)}
                    >
                        <div className="circle">3</div>
                        <span className="label">Payment</span>
                    </div>
                </div>


                {activeTab === 0 && (
                    <div className="check_user_section">
                        <h3>Select or Add Address</h3>

                       <div className="check_user_address_list">
                    {userAddresses.map((addr) => (
                        <div
                        key={addr.id}
                        className={`check_user_address_card ${selectedAddress?.id === addr.id ? 'check_user_address_selected' : ''}`}
                        onClick={() => setSelectedAddress(addr)}
                        >
                        <div className="address_card_header">
                            <h4>{addr.addressLine1 || 'Missing Address'}</h4>

                            {/* 🗑️ Delete Icon */}
                            <button
                            className="delete_address_icon"
                            onClick={(e) => {
                                e.stopPropagation();
                               deleteAddress(addr.id, currentUser, dispatch);
                            }}
                            >
                            <Trash2 size={16} />
                            </button>
                        </div>

                        {addr.addressLine2 && <p>{addr.addressLine2}</p>}
                        <p>{addr.city}, {addr.state || ''}, {addr.country}</p>
                        <p>Postal Code: {addr.postalCode}</p>

                        {addr.isDefault ? (
                            <span className="check_user_default_badge">Default</span>
                        ) : (
                            <button
                            className="set_default_button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setDefaultAddress(addr, currentUser, dispatch, setIsProcessing, setSelectedAddress);
                            }}
                            >
                            Set as Default
                            </button>
                        )}
                        </div>
                    ))}
                    </div>

                      <div className="check_user_new_address">
                    <h4>Add New Address</h4>

                    <div className="check_user_form_row">
                        <div className="check_user_form_group">
                        <input
                            placeholder="Address Line 1 *"
                            value={newAddress.addressLine1}
                            onChange={(e) => setNewAddress({ ...newAddress, addressLine1: e.target.value })}
                            className={errors.addressLine1 ? 'check_user_input_error' : ''}
                        />
                        {errors.addressLine1 && <span className="check_user_error">{errors.addressLine1}</span>}
                        </div>
                        <div className="check_user_form_group">
                        <input
                            placeholder="Address Line 2"
                            value={newAddress.addressLine2}
                            onChange={(e) => setNewAddress({ ...newAddress, addressLine2: e.target.value })}
                        />
                        </div>
                    </div>

                    <div className="check_user_form_row">
                        <div className="check_user_form_group">
                        <input
                            placeholder="City *"
                            value={newAddress.city}
                            onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                            className={errors.city ? 'check_user_input_error' : ''}
                        />
                        {errors.city && <span className="check_user_error">{errors.city}</span>}
                        </div>
                        <div className="check_user_form_group">
                        <input
                            placeholder="State"
                            value={newAddress.state}
                            onChange={(e) => setNewAddress({ ...newAddress, state: e.target.value })}
                        />
                        </div>
                    </div>

                    <div className="check_user_form_row">
                        <div className="check_user_form_group">
                        <input
                            placeholder="Postal Code *"
                            value={newAddress.postalCode}
                            onChange={(e) => setNewAddress({ ...newAddress, postalCode: e.target.value })}
                            className={errors.postalCode ? 'check_user_input_error' : ''}
                        />
                        {errors.postalCode && <span className="check_user_error">{errors.postalCode}</span>}
                        </div>
                        <div className="check_user_form_group">
                        <input
                            placeholder="Country"
                            value={newAddress.country}
                            onChange={(e) => setNewAddress({ ...newAddress, country: e.target.value })}
                            disabled
                        />
                        </div>
                    </div>

                    <button
                        onClick={handleAddAddress}
                        disabled={isProcessing}
                        className="check_user_btn check_user_btn_primary"
                    >
                        {isProcessing ? 'Adding...' : 'Add & Continue'}
                    </button>

                    {selectedAddress && (
                        <button
                        className="check_user_btn check_user_btn_continue"
                        onClick={() => setActiveTab(1)}
                        disabled={isProcessing}
                        >
                        Continue with Selected Address
                        </button>
                    )}
                    </div>

                    </div>
                )}

                {activeTab === 1 && (
                    <div className="check_user_section">
                        <div className="check_user_summary_grid">
                            <div className="check_user_order_summary">
                                <h3>Order Summary</h3>
                                <div className="check_user_cart_items">
                                    {cartProducts.map(item => (
                                        <div key={item.id} className="check_user_cart_item">
                                            <div className="check_user_item_img">
                                                {item.images && item.images.length > 0 && (
                                                    <img src={item.images[0]} alt={item.name} />
                                                )}
                                            </div>
                                            <div className="check_user_item_details">
                                                <h4>{item.name}</h4>
                                                <p>Quantity: {item.quantity}</p>
                                                <p>Price: ₹{item.price.toFixed(2)}</p>
                                            </div>
                                            <div className="check_user_item_total">
                                                ₹{(item.price * item.quantity).toFixed(2)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="check_user_order_total">
                                    <h4>Total: ₹{totalAmount.toFixed(2)}</h4>
                                </div>
                            </div>

                            <div className="check_user_address_summary">
                                <h3>Delivery Address</h3>
                                {selectedAddress ? (
                                    <div className="check_user_selected_address">
                                        <h4>{selectedAddress.addressLine1 || 'Missing Address'}</h4>
                                        {selectedAddress.addressLine2 && <p>{selectedAddress.addressLine2}</p>}
                                        <p>{selectedAddress.city}, {selectedAddress.state || ''}, {selectedAddress.country}</p>
                                        <p>Postal Code: {selectedAddress.postalCode}</p>
                                        <button
                                            className="check_user_change_address"
                                            onClick={() => setActiveTab(0)}
                                        >
                                            Change Address
                                        </button>
                                    </div>
                                ) : (
                                    <NoData message="No address selected. Please add an address to proceed." />
                                )}
                            </div>
                        </div>

                        <div className="check_user_nav_buttons">
                            <button
                                className="check_user_btn check_user_btn_back"
                                onClick={() => setActiveTab(0)}
                            >
                                Back
                            </button>
                            <button
                                className="check_user_btn check_user_btn_continue"
                                onClick={() => setActiveTab(2)}
                                disabled={!selectedAddress}
                            >
                                Proceed to Payment
                            </button>
                        </div>
                    </div>
                )}

                {activeTab === 2 && (
                    <div className="check_user_section">
                        <div className="check_user_payment_grid">
                            {/* <div className="check_user_payment_methods">
                                <h3>Payment Method</h3>
                                <div className="check_user_payment_options">
                                    <label className={`check_user_payment_option ${paymentMethod === 'credit-card' ? 'check_user_payment_selected' : ''}`}>
                                        <input
                                            type="radio"
                                            name="payment"
                                            value="credit-card"
                                            checked={paymentMethod === 'credit-card'}
                                            onChange={() => setPaymentMethod('credit-card')}
                                        />
                                        <span>Credit/Debit Card</span>
                                        <div className="check_user_card_icons">
                                            <span>💳</span>
                                            <span>VISA</span>
                                            <span>MC</span>
                                        </div>
                                    </label>

                                    <label className={`check_user_payment_option ${paymentMethod === 'upi' ? 'check_user_payment_selected' : ''}`}>
                                        <input
                                            type="radio"
                                            name="payment"
                                            value="upi"
                                            checked={paymentMethod === 'upi'}
                                            onChange={() => setPaymentMethod('upi')}
                                        />
                                        <span>UPI</span>
                                        <span>📱</span>
                                    </label>

                                     <label className={`check_user_payment_option ${paymentMethod === 'cod' ? 'check_user_payment_selected' : ''}`}>
                                        <input
                                            type="radio"
                                            name="payment"
                                            value="cod"
                                            checked={paymentMethod === 'cod'}
                                            onChange={() => setPaymentMethod('cod')}
                                        />
                                        <span>Cash on Delivery</span>
                                        <span>💰</span>
                                    </label> 
                                </div>

                                {paymentMethod === 'credit-card' && (
                                    <div className="check_user_card_details">
                                        <div className="check_user_form_row">
                                            <div className="check_user_form_group">
                                                <input placeholder="Card Number" />
                                            </div>
                                        </div>
                                        <div className="check_user_form_row">
                                            <div className="check_user_form_group">
                                                <input placeholder="MM/YY" />
                                            </div>
                                            <div className="check_user_form_group">
                                                <input placeholder="CVV" />
                                            </div>
                                        </div>
                                        <div className="check_user_form_row">
                                            <div className="check_user_form_group">
                                                <input placeholder="Cardholder Name" />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {paymentMethod === 'upi' && (
                                    <div className="check_user_upi_details">
                                        <div className="check_user_form_group">
                                            <input placeholder="UPI ID (e.g., name@upi)" />
                                        </div>
                                        <p className="check_user_hint">You'll be redirected to your UPI app for payment</p>
                                    </div>
                                )}
                            </div> */}

                            <div className="check_user_order_summary_payment">
                                <h3>Order Summary</h3>
                                <div className="check_user_summary_items">
                                    {cartProducts.map(item => (
                                        <div key={item.id} className="check_user_summary_item">
                                            <span>{item.name} × {item.quantity}</span>
                                            <span>₹{(item.price * item.quantity).toFixed(2)}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="check_user_summary_totals">
                                    <div className="check_user_summary_row">
                                        <span>Subtotal</span>
                                        <span>₹{totalAmount.toFixed(2)}</span>
                                    </div>
                                    <div className="check_user_summary_row">
                                        <span>Shipping</span>
                                        <span>FREE</span>
                                    </div>
                                    <div className="check_user_summary_row check_user_summary_total">
                                        <span>Total</span>
                                        <span>₹{totalAmount.toFixed(2)}</span>
                                    </div>
                                </div>

                                <div className="check_user_address_summary_payment">
                                    <h4>Deliver to:</h4>
                                    {selectedAddress && (
                                        <>
                                        {selectedAddress.addressLine1 && <p>{selectedAddress.addressLine1}</p>}
                                         {selectedAddress.addressLine2 && <p>{selectedAddress.addressLine2}</p>}
                                            <p>{selectedAddress.city}, {selectedAddress.state || ''}, {selectedAddress.country}</p>
                                            <p>Postal Code: {selectedAddress.postalCode}</p>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="check_user_nav_buttons">
                            <button
                                className="check_user_btn check_user_btn_back"
                                onClick={() => setActiveTab(1)}
                            >
                                Back
                            </button>
                            <button
                                className="check_user_btn check_user_btn_pay"
                                onClick={handlePlaceOrder}
                                disabled={isProcessing}
                            >
                                {isProcessing ? 'Processing...' : 'Pay Now'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
            <Footer />
        </>
    );
};

export default CheckoutPage;